import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ClientService, ContactService, ProjectService } from "@services";
import utils from "@utils";
import { ReactSelect, Loader, AddContactModal } from "../../../../../components";

import "./ProjectForm.scss";

const ProjectForm = props => {
	const { lang } = global;
	const { user, router, project } = props;

	const paymentTerms = [
		{ value: 10, label: lang.t("On Site") },
		{ value: 20, label: `${lang.t("Net")}, 15` },
		{ value: 30, label: `${lang.t("Net")}, 30` },
		{ value: 40, label: `${lang.t("Net")}, 60` },
		{ value: 50, label: `${lang.t("Net")}, 90` },
	];

	const initialValidations = {
		Name: true,
		ClientId: true,
	};

	const [loading, setLoading] = useState(true);

	const [clients, setClients] = useState({
		options: [],
		loading: false,
	});

	const [clientSupervisors, setClientSupervisors] = useState({
		options: [],
		loading: false,
	});

	const [agencySupervisors, setAgencySupervisors] = useState({
		options: [],
		loading: false,
	});

	const [addContactModal, setAddContactModal] = useState({
		showAddContactModal: false,
		addContactModalType: "",
	});

	const [form, setForm] = useState({
		Name: "",
		ProjectNo: "",
		ClientId: "",
		AgencySupervisorName: "",
		ClientSupervisorContactId: "",
		AgencySupervisorContactId: "",
		PaymentTermsId: "",
		Description: "",
		IsAgencyRKPayEnabled: false,
		ProjectStatus: "1",
		SecondaryProjectSupervisorModel: [{}],
	});

	const [validations, setValidations] = useState(initialValidations);

	const updateInput = ({ target: { name, value, type, checked } }) => {
		if (type === "checkbox") {
			setForm(prevForm => ({ ...prevForm, [name]: checked }));
		} else {
			setForm(prevForm => ({ ...prevForm, [name]: value }));
		}
	};

	const updateSelect = ({ value, label }, { name }) => {
		// set agency supervisor name if that is what's being changed
		setForm(prevForm => ({
			...prevForm,
			AgencySupervisorName:
				name === "AgencySupervisorContactId" ? label : prevForm.AgencySupervisorName,
			[name]: value,
		}));
	};

	const updatePaymentTerms = ({ target: { value } }) => {
		setForm(prevForm => ({
			...prevForm,
			PaymentTermsId: value,
		}));
	};

	/** Creates a new input which allows for adding additional agency contacts */
	const changeAdditionalAgencyContacts = index => () => {
		setForm(prevForm => {
			const newForm = { ...prevForm };

			// The first additional contact action is to add more contacs, the next ones action is for delete themselves.
			if (index === 0) {
				newForm.SecondaryProjectSupervisorModel.push({
					SecondaryProjectSupervisorId: "",
					SecondarySupervisorName: "",
				});
			} else {
				newForm.SecondaryProjectSupervisorModel.splice(index, 1);
			}

			return newForm;
		});
	};

	const updateAdditionalAgencyContact = index => event => {
		if (!event) {
			setForm(prevForm => {
				const newForm = { ...prevForm };
				newForm.SecondaryProjectSupervisorModel[0] = {};

				return newForm;
			});
		} else {
			const { value: SecondaryProjectSupervisorId, label: SecondarySupervisorName } = event;
			setForm(prevForm => {
				const newForm = { ...prevForm };
				newForm.SecondaryProjectSupervisorModel[index] = {
					SecondaryProjectSupervisorId,
					SecondarySupervisorName,
				};

				return newForm;
			});
		}
	};

	/** Retrieves a list of clients */
	const getClients = () => {
		setClients({ options: [], loading: true });

		ClientService.getAll({
			includeInactive: true,
			getDetails: true,
		})
			.then(({ data }) => {
				const options = data.map(client => ({
					value: client.Id,
					label: client.Name,
					isAgencyRKPayEnabled: client.IsAgencyRKPayEnabled,
					isRKPay: client.IsRKPay,
				}));

				setClients({ options, loading: false });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading clients"),
					title: "Error",
				});
			})
			.finally(() => setLoading(false));
	};

	/**
	 * Retrieves al ist of client supervisors associated with the selected client
	 * @param {Number} clientId  The uniqe id of the selected client
	 */
	const getClientSupervisors = (clientId = form.ClientId) => {
		setClientSupervisors({ options: [], loading: true });
		ContactService.getAll({ clientId, isOrganization: false })
			.then(response => {
				const options = response.data.ItemList.map(contact => ({
					value: contact.Id,
					label: contact.ContactName,
				}));

				setClientSupervisors({ options, loading: false });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading contacts"),
					title: "Error",
				});
			});
	};

	/**
	 * Retrieves al ist of agency supervisors associated with the organization
	 * @param {Number} organizationId  The uniqe id of the organization the user is associated with
	 */
	const getAgencySupervisors = (organizationId = user.Organization.Id) => {
		setAgencySupervisors({ options: [], loading: true });

		ContactService.getAll({ clientId: organizationId, isOrganization: true })
			.then(response => {
				const options = response.data.ItemList.map(contact => ({
					value: contact.Id,
					label: contact.ContactName,
				}));

				setAgencySupervisors({ options, loading: false });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading contacts"),
					title: "Error",
				});
			});
	};

	const openAddContactModal = type => {
		setAddContactModal({
			...addContactModal,
			showAddContactModal: true,
			addContactModalType: type,
		});
	};

	const closeAddContactModal = () => {
		const { addContactModalType } = addContactModal;
		if (addContactModalType === "addAgencySupervisor") {
			getAgencySupervisors();
		} else {
			getClientSupervisors();
		}
		setAddContactModal({ showAddContactModal: false, addContactModalType: "" });
	};

	const validateForm = () => {
		let valid = true;

		// reset validations
		setValidations(initialValidations);

		// validation criterias
		const Name = form.Name !== "";
		const ClientId = typeof form.ClientId === "number";
		valid = Name && ClientId;
		// set validation state if failed
		if (!valid) {
			setValidations({
				Name,
				ClientId,
			});
		}

		return valid;
	};

	/** Gathers all required information pertaining to the project form and saves any changes made to the project */
	const saveProject = () => {
		const {
			params: { projectId },
		} = router;
		if (validateForm()) {
			setLoading(true);

			// if there's no secondary supervisors, send an empty array instead of an array with an empty object.
			const SecondaryProjectSupervisorModel = Object.keys(
				form.SecondaryProjectSupervisorModel[0],
			).length
				? form.SecondaryProjectSupervisorModel
				: [];

			ProjectService.saveOrUpdate({ ...form, SecondaryProjectSupervisorModel }, projectId)
				.then(() => {
					utils.notify({
						type: "success",
						message: projectId
							? lang.t("Project successfully updated")
							: lang.t("Project sucessfully created"),
					});
					router.push("/projects");
				})
				.catch(({ response }) => {
					let message = `${lang.t("Error")} ${projectId ? lang.t("Updating") : lang.t("Creating")} ${lang.t("Project")}`;

					if (response.status === 409) {
						message = response.data.Message;
					}

					utils.notify({
						message,
						type: "error",
						title: "Error",
					});
				})
				.finally(() => setLoading(false));
		}
	};

	// Load clients and agency supervisor contacts
	useEffect(() => {
		getClients();
		getAgencySupervisors(user.Organization.Id);
	}, []);

	// load project info if editing
	useEffect(() => {
		if (project) setForm(project);
	}, [project]);

	useEffect(() => {
		// Reload client supervisor contacts on client change
		if (form.ClientId) getClientSupervisors(form.ClientId);
	}, [form.ClientId, clients]);

	return (
		<Fragment>
			<div
				id="project-form"
				className="container-fluid"
			>
				<div className={`form-group row ${validations.Name ? "" : "has-error"}`}>
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="projectName"
						>{`${lang.t("Project Name")}:`}</label>
					</div>
					<div className="col-sm-7 col-md-8">
						<input
							id="projectName"
							name="Name"
							className="form-control"
							onChange={updateInput}
							value={form.Name}
							maxLength="50"
						/>
					</div>
				</div>
				<div className="form-group row">
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="projectNumber"
						>{`${lang.t("Project Number")}:`}</label>
					</div>
					<div className="col-sm-7 col-md-8">
						<input
							id="projectNumber"
							type="number"
							min="0"
							name="ProjectNo"
							className="form-control"
							placeholder={lang.t("No dashes (ex 998888888)")}
							onChange={updateInput}
							value={form.ProjectNo}
						/>
					</div>
				</div>
				<div className="row separator" />
				<div className={`form-group row ${validations.ClientId ? "" : "has-error"}`}>
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="clientId"
						>{`${lang.t("Client")}:`}</label>
					</div>
					<div className="col-sm-7 col-md-8">
						<ReactSelect
							id="clientId"
							name="ClientId"
							options={clients.options}
							isLoading={clients.loading}
							onChange={updateSelect}
							value={{
								value: form.ClientId || "",
								label:
									clients.options.find(c => c.value === form.ClientId)?.label ||
									"",
							}}
						/>
					</div>
				</div>
				<div className="form-group row">
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="clientSupervisorId"
						>{`${lang.t("Client Supervisor")}:`}</label>
					</div>
					<div className="col-xs-10 col-sm-7 col-md-8">
						<ReactSelect
							id="clientSupervisorId"
							name="ClientSupervisorContactId"
							options={clientSupervisors.options}
							isLoading={clientSupervisors.loading}
							onChange={updateSelect}
							value={{
								value: form.ClientSupervisorContactId || "",
								label:
									clientSupervisors.options.find(
										s => s.value === form.ClientSupervisorContactId,
									)?.label || "",
							}}
						/>
					</div>
					<div className="col-xs-2 col-md-1">
						<button
							type="button"
							id="addClientSupervisor"
							className="btn btn-primary btn-xs btn-block"
							onClick={() => openAddContactModal("addClientSupervisor")}
							disabled={!form.ClientId}
						>
							+
						</button>
					</div>
				</div>
				<div className="form-group row">
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="agencySupervisorId"
						>{`${lang.t("Agency Supervisor")}:`}</label>
					</div>
					<div className="col-xs-10 col-sm-7 col-md-8">
						<ReactSelect
							id="agencySupervisorId"
							name="AgencySupervisorContactId"
							options={agencySupervisors.options}
							isLoading={agencySupervisors.loading}
							onChange={updateSelect}
							value={{
								value: form.AgencySupervisorContactId || "",
								label:
									agencySupervisors.options.find(
										s => s.value === form.AgencySupervisorContactId,
									)?.label || "",
							}}
						/>
					</div>
					<div className="col-xs-2 col-md-1">
						<button
							type="button"
							id="addAgencySupervisor"
							className="btn btn-primary btn-xs btn-block"
							onClick={() => openAddContactModal("addAgencySupervisor")}
						>
							+
						</button>
					</div>
				</div>
				<For
					each="additionalContact"
					index="additionalContactIdx"
					of={form.SecondaryProjectSupervisorModel}
				>
					<div
						key={`additionalContact-${additionalContactIdx}`}
						className="form-group row"
					>
						<div className="col-sm-3">
							<label
								className="control-label"
								htmlFor={`additionalContact-${additionalContactIdx}`}
							>
								{`${lang.t("Additional Agency Contact")}:`}
							</label>
						</div>
						<div className="col-xs-10 col-sm-7 col-md-8">
							<ReactSelect
								id={`additionalContact-${additionalContactIdx}`}
								options={agencySupervisors.options}
								isLoading={agencySupervisors.loading}
								onChange={updateAdditionalAgencyContact(additionalContactIdx)}
								isClearable={
									additionalContactIdx === 0 &&
									additionalContact.SecondaryProjectSupervisorId
								}
								value={{
									value: additionalContact?.SecondaryProjectSupervisorId || "",
									label: additionalContact?.SecondarySupervisorName || "",
								}}
							/>
						</div>
						<div className="col-xs-2 col-md-1">
							<button
								type="button"
								id="addAgencySupervisor"
								className={`btn btn-${additionalContactIdx === 0 ? "primary" : "danger"} btn-xs btn-block`}
								onClick={changeAdditionalAgencyContacts(additionalContactIdx)}
							>
								{additionalContactIdx === 0 ? "+" : "-"}
							</button>
						</div>
					</div>
				</For>
				<div className="row separator" />
				<div className="form-group row">
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="paymentTerms"
						>{`${lang.t("Payment Terms")}:`}</label>
					</div>
					<div className="col-sm-8">
						<select
							id="paymentTerms"
							name="PaymentTermsId"
							className="form-control"
							onChange={e => updatePaymentTerms(e)}
							value={form.PaymentTermsId}
						>
							<option value="">{lang.t("Select")}</option>
							{paymentTerms.map(({ value, label }) => (
								<option
									key={value}
									value={value}
								>
									{label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="form-group row">
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="description"
						>{`${lang.t("Description")}:`}</label>
					</div>
					<div className="col-sm-8">
						<textarea
							id="description"
							name="Description"
							className="form-control"
							maxLength="100"
							onChange={updateInput}
							value={form.Description}
						/>
					</div>
				</div>
				<div className="form-group row">
					<div className="col-sm-3">
						<label
							className="control-label"
							htmlFor="status"
						>{`${lang.t("Status")}:`}</label>
					</div>
					<div className="col-sm-8">
						<div className="radio-container">
							<div className="radio">
								<label
									title="status: active"
									htmlFor="status-active"
								>
									<input
										id="status-active"
										name="ProjectStatus"
										type="radio"
										value="1"
										onChange={updateInput}
										checked={form.ProjectStatus.toString() === "1"}
									/>
									{lang.t("Active")}
								</label>
							</div>
							<div className="radio">
								<label
									title="status: inactive"
									htmlFor="status-inactive"
								>
									<input
										id="status-inactive"
										name="ProjectStatus"
										type="radio"
										value="0"
										onChange={updateInput}
										checked={form.ProjectStatus.toString() === "0"}
									/>
									{lang.t("Inactive")}
								</label>
							</div>
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-xs-4 col-xs-offset-2 col-sm-3 col-sm-offset-3">
						<button
							type="button"
							id="save-button"
							className="btn btn-primary btn-block"
							onClick={saveProject}
						>
							{lang.t("Save")}
						</button>
					</div>
					<div className="col-xs-4 col-sm-3">
						<button
							type="button"
							id="cancel-button"
							onClick={() => router.push("/projects")}
							className="btn btn-link btn-block"
						>
							{lang.t("Cancel")}
						</button>
					</div>
				</div>
			</div>
			<AddContactModal
				showModal={addContactModal.showAddContactModal}
				closeModal={closeAddContactModal}
				clientId={
					addContactModal.addContactModalType === "addClientSupervisor"
						? form.ClientId
						: undefined
				}
				organizationId={
					addContactModal.addContactModalType !== "addClientSupervisor"
						? user.Organization.Id
						: undefined
				}
			/>
			<Loader active={loading} />
		</Fragment>
	);
};

ProjectForm.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object.isRequired,
	project: PropTypes.object,
};

ProjectForm.defaultProps = {
	user: {},
	project: undefined,
};

export default ProjectForm;
